import * as mutations from "../../graphql/mutations";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DeletionModal from "../../components/modals/DeletionModal";
import LoadingModal from "../../components/modals/LoadingModal";
import NotificationSnackbar from "../../components/NotificationSnackbar";
import DataTable from "../../components/DataTable";
import NewAssessmentModal from "../../components/modals/NewAssessmentModal";
import SpecialButton from "../../components/SpecialButton";
import ImageUpload from "../../components/ImageUpload";
import { apiClient } from "../../helpers/apiClient";
import { uploadData } from "@aws-amplify/storage";
import { v4 as uuid } from "uuid";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { versions, CGI_RED } from "../../helpers/enums";
import { useWindowDimensions } from "../../helpers/helpers";
import {
  fetchAllAssessments,
  fetchAssessmentAssignmentByAssessment,
  fetchModulesByAssessment,
  fetchQuestionsByModule,
  fetchRequirementsByQuestion,
  fetchStructuredEntriesByModule,
} from "../../helpers/apiRequests";
import {
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";

export default function ManageAssessments() {
  const emptyAssessment = {
    id: "",
    name: "",
    description: "",
    type: versions.STANDARD,
    active: false,
    public: false,
    modules: [],
  };

  const { t } = useTranslation();

  const emptyIdAndName = {
    id: "",
    name: "",
  };

  const [openModal, setOpenModal] = useState(false);
  const [openLoading, setOpenLoading] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openNotification, setOpenNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");

  const [toDeleteIdAndName, setToDeleteIdAndName] = useState(emptyIdAndName);
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(emptyAssessment);

  const { width } = useWindowDimensions();

  const hiddenHeaders = ["id"];
  const exclude = ["description"];
  const smallerFields = ["active", "language"];
  const biggerFields = ["name"];

  const headers =
    assessments.length > 0
      ? [
          ...Object.keys(assessments[0])
            .filter((key) => !exclude.includes(key))
            .map((key) => {
              return {
                field: key,
                headerName: t(key),
                flex: smallerFields.includes(key)
                  ? 1
                  : biggerFields.includes(key)
                    ? 3
                    : 2,
                hide: hiddenHeaders.includes(key),
                renderCell: (params) => {
                  const id = params.row.id;
                  const { field, value } = params;
                  const specialFields = ["imageKey"];
                  let finalValue;
                  if (value === null) {
                    finalValue = specialFields.includes(field) ? null : false;
                  } else {
                    finalValue = value;
                  }
                  if (specialFields.includes(field)) {
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexDirection: "row",
                          height: "100%",
                        }}
                      >
                        <ImageUpload
                          imageKey={finalValue}
                          uploadImage={uploadImage}
                          productId={id}
                        />
                      </Box>
                    );
                  }
                  if (typeof finalValue === "boolean") {
                    return (
                      <Switch
                        size="small"
                        checked={finalValue}
                        onChange={(event) => {
                          updateAssessmentState(id, event.target.checked);
                        }}
                      />
                    );
                  }
                  return <span>{finalValue}</span>;
                },
              };
            }),
          {
            field: "manage",
            headerName: t("manageAssessment"),
            flex: 3,
            sortable: false,
            renderCell: ({ row }) => {
              const assessmentId = row.id;
              const assessment = row;
              return (
                <>
                  <Tooltip title={t("editTitle")}>
                    <IconButton
                      onClick={() => setSelectedAssessment(assessment)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>{" "}
                  <Tooltip title={t("deleteAssessment")}>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setToDeleteIdAndName({
                          id: assessmentId,
                          name: assessment.name,
                        });
                      }}
                    >
                      <DeleteForeverIcon />
                    </IconButton>
                  </Tooltip>
                  <SpecialButton
                    buttontext={t("manageAssessment")}
                    routingPath={"manageAssessments"}
                    id={assessmentId}
                    disabled={false}
                  />
                </>
              );
            },
          },
        ]
      : [];

  const rows = assessments.map((row, index) => ({
    id: row.id ?? index,
    ...row,
  }));

  async function fetchAssessments() {
    setOpenLoading(true);
    try {
      const fetchedAssessments = await fetchAllAssessments();
      const allAssessments = fetchedAssessments.map((assessment) => ({
        id: assessment.id,
        name: assessment.name,
        description: assessment.description,
        contactPerson: assessment.contactPerson,
        type: versions[assessment.type],
        imageKey: assessment.imageKey,
        language: assessment.language,
        active: assessment.active,
      }));
      setAssessments(allAssessments);
    } catch (e) {
      console.error(e);
      setNotificationMessage(t("errorFetchDataMessage"));
      setOpenNotification(true);
    }
    setOpenLoading(false);
  }

  async function deleteAssessment(toDeleteId) {
    setOpenLoading(true);

    try {
      const safe = (v) => (Array.isArray(v) ? v : []);

      if (!toDeleteId) {
        setNotificationMessage(t("errorMessage"));
        setOpenNotification(true);
        return;
      }

      const assessmentAssignments = safe(
        await fetchAssessmentAssignmentByAssessment(toDeleteId),
      );

      if (assessmentAssignments.length > 0) {
        await Promise.all(
          assessmentAssignments.map((a) =>
            apiClient.graphql({
              query: mutations.deleteAssessmentAssignment,
              variables: { input: { id: a.id } },
            }),
          ),
        );
      }

      const modulesByAssessment = safe(
        await fetchModulesByAssessment(toDeleteId),
      );

      await Promise.all(
        modulesByAssessment.map(async (module) => {
          const questionsByModule = safe(
            await fetchQuestionsByModule(module.id),
          );

          await Promise.all(
            questionsByModule.map(async (question) => {
              const requirementsByQuestion = safe(
                await fetchRequirementsByQuestion(question.id),
              );

              if (requirementsByQuestion.length > 0) {
                await Promise.all(
                  requirementsByQuestion.map((req) =>
                    apiClient.graphql({
                      query: mutations.deleteRequirement,
                      variables: { input: { id: req.id } },
                    }),
                  ),
                );
              }

              await apiClient.graphql({
                query: mutations.deleteQuestion,
                variables: { input: { id: question.id } },
              });
            }),
          );

          const structuredEntries = safe(
            await fetchStructuredEntriesByModule(module.id),
          );

          if (structuredEntries.length > 0) {
            await Promise.all(
              structuredEntries.map((se) =>
                apiClient.graphql({
                  query: mutations.deleteStructuredEntry,
                  variables: { input: { id: se.id } },
                }),
              ),
            );
          }
        }),
      );

      if (modulesByAssessment.length > 0) {
        await Promise.all(
          modulesByAssessment.map((m) =>
            apiClient.graphql({
              query: mutations.deleteModule,
              variables: { input: { id: m.id } },
            }),
          ),
        );
      }

      await apiClient.graphql({
        query: mutations.deleteAssessment,
        variables: { input: { id: toDeleteId } },
      });

      await fetchAssessments();
      setNotificationMessage(t("itemDeleted!"));
      setToDeleteIdAndName({ id: "", name: "" });
      setOpenDelete(false);
    } catch (e) {
      console.error("deleteAssessment failed:", e);
      setNotificationMessage(t("errorMessage"));
    } finally {
      setOpenNotification(true);
      setOpenLoading(false);
    }
  }

  async function uploadImage(e, moduleId) {
    const file = e.target.files[0];
    if (!file) return;

    const newImgUuid = "modules/" + uuid();

    try {
      await uploadData({
        key: newImgUuid,
        data: file,
      });

      await apiClient.graphql({
        query: mutations.updateAssessment,
        variables: {
          input: {
            id: moduleId,
            imageKey: newImgUuid,
          },
        },
      });

      fetchAssessments();
      setNotificationMessage(t("uploadImageSucces"));
    } catch (error) {
      console.error(error);
      setNotificationMessage(t("uploadError"));
    }
    setOpenNotification(true);
  }

  async function updateAssessmentState(assessmentId, newState) {
    try {
      await apiClient.graphql({
        query: mutations.updateAssessment,
        variables: {
          input: {
            id: assessmentId,
            active: newState,
          },
        },
      });
      fetchAssessments();
      setNotificationMessage(t("assessmentStatusUpdated"));
    } catch (e) {
      setNotificationMessage(t("errorMessage"));
    }
    setOpenNotification(true);
  }

  useEffect(() => {
    setAssessments([]);
    fetchAssessments();
  }, []);

  useEffect(() => {
    if (selectedAssessment.id !== "") {
      setOpenModal(true);
    }
  }, [selectedAssessment]);

  useEffect(() => {
    if (toDeleteIdAndName.id !== "") {
      setOpenDelete(true);
    }
  }, [toDeleteIdAndName]);

  return (
    <Container maxWidth="xl">
      <Box sx={{ width: 1, display: "flex", flexDirection: "column", mt: 5 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: CGI_RED,
              fontWeight: 500,
              fontSize: 23,
            }}
          >
            {t("manageAssessments")}
          </Typography>
        </Box>
        <Divider sx={{ mt: 1 }} />
        {assessments.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              fontStyle: "italic",
              mt: 4,
            }}
          >
            <Typography sx={{ fontFamily: "Inter", fontSize: 16 }}>
              {t("noAssessmentsYet")}
            </Typography>
          </Box>
        )}
        <DataTable
          headers={headers}
          data={rows}
          hiddenColumns={hiddenHeaders}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ alignSelf: "center" }}
          onClick={() => setOpenModal(true)}
        >
          {t("createNewAssesment")}
        </Button>
      </Box>
      <NewAssessmentModal
        dataLabel={t("newAssessment")}
        open={openModal}
        setOpen={setOpenModal}
        selectedAssessment={selectedAssessment}
        setOpenNotification={setOpenNotification}
        setNotificationMessage={setNotificationMessage}
        fetchAllAssessments={fetchAssessments}
      />
      <LoadingModal open={openLoading} />
      <NotificationSnackbar
        message={notificationMessage}
        open={openNotification}
        setOpen={setOpenNotification}
      />
      <DeletionModal
        open={openDelete}
        setOpen={setOpenDelete}
        deleteFunction={deleteAssessment}
        safeDelete={true}
        itemClass={"Assessment"}
        name={toDeleteIdAndName.name}
        id={toDeleteIdAndName.id}
      />
    </Container>
  );
}
